import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import ShoppingListDetail from "../components/ShoppingListDetail";
import axios from "axios";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { useSocket } from "../contexts/SocketContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMoon, faSun } from "@fortawesome/free-solid-svg-icons";

const ShoppingListSoloPage = () => {
  const [shoppingList, setShoppingList] = useState(null);
  const [itemSuggestions, setItemSuggestions] = useState([]);
  const { id } = useParams();
  const { theme, toggleTheme } = useTheme();
  const { authenticated, loading } = useAuth();
  const { socket } = useSocket();

  const fetchShoppingList = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/shopping-lists/${id}`
      );
      setShoppingList(response.data);
    } catch (error) {
      console.error("Error fetching shopping list:", error);
    }
  };

  const fetchItemSuggestions = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/shopping-lists/suggestions/items`
      );
      setItemSuggestions(response.data);
    } catch (error) {
      console.error("Error fetching item suggestions:", error);
    }
  };

  // Handle real-time updates from socket
  const handleSocketUpdate = useCallback((data) => {
    const { listId, list, eventType } = data;
    
    // Only handle updates for this specific list
    if (listId !== id) return;
    
    if (eventType === "deleted") {
      // List was deleted - could redirect or show message
      setShoppingList(null);
    } else if (list) {
      // Update the list with new data
      setShoppingList(list);
    }
    
    // Refresh suggestions when items change
    if (eventType === "item-added") {
      fetchItemSuggestions();
    }
  }, [id]);

  useEffect(() => {
    fetchShoppingList();
    // Only fetch suggestions when authenticated (requires auth on server)
    if (authenticated) {
      fetchItemSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, authenticated]);

  // Subscribe to socket events for real-time updates
  useEffect(() => {
    if (!socket || !id) return;

    // Join the specific list room for targeted updates
    socket.emit("join-list", id);

    // Listen for updates to this specific list
    socket.on("shopping-list:updated", handleSocketUpdate);

    return () => {
      socket.emit("leave-list", id);
      socket.off("shopping-list:updated", handleSocketUpdate);
    };
  }, [socket, id, handleSocketUpdate]);

  if (!shoppingList) {
    return <div>Loading...</div>;
  }

  const setShoppingLists = (updater) => {
    if (typeof updater === "function") {
      setShoppingList(updater([shoppingList])[0]);
    } else {
      setShoppingList(updater);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 bg-white dark:bg-dark-surface shadow-md rounded-lg min-h-screen transition-colors">
      <div className="flex justify-end mb-4">
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          title="Toggle theme"
        >
          <FontAwesomeIcon icon={theme === 'dark' ? faSun : faMoon} className="text-gray-700 dark:text-gray-200" />
        </button>
      </div>
      <ShoppingListDetail
        list={shoppingList}
        setShoppingLists={setShoppingLists}
        itemSuggestions={itemSuggestions}
        onItemAdded={fetchItemSuggestions}
      />
    </div>
  );
};

export default ShoppingListSoloPage;
